import React from "react";
import { TicketPercent } from "lucide-react";
import {
  BottomSheet,
  Button,
  EmptyState,
  Modal,
  failedEmptyState,
  useIsMobile,
  useToast,
} from "../../../components/ui";
import CouponInput from "./CouponInput";
import type { CouponsState } from "../hooks/useCoupons";
import { planTermLabel } from "../pricing";
import type {
  AppliedCoupon,
  BillingCycleId,
  Coupon,
  SubscriptionPlan,
} from "../types/billing.types";

interface AvailableCouponsModalProps {
  open: boolean;
  onClose: () => void;
  /** The list, and what became of fetching it — see `useCoupons`. */
  coupons: CouponsState;
  /** The coupon on the order right now, or `null` — the whole record rather
   *  than the code, because the field inside this dialog draws from it the
   *  same way the one on the checkout does. */
  applied: AppliedCoupon | null;
  /** The term being bought, which decides whether a coupon is one to apply or
   *  one to switch plan for. */
  cycle: BillingCycleId;
  /** What is on sale, so a "Switch plan" can name the term it would move to. */
  plans: SubscriptionPlan[];
  /** Puts this code on the order. The dialog closes once it holds. */
  onApply: (code: string) => Promise<void>;
  onRemove: () => Promise<void>;
  /** Takes the buyer to the plan list, to pick the term this coupon is good
   *  on. The term and the code are passed for what the screen wants to do with
   *  them; where it lands is the screen's to decide — a trial picks its plan
   *  on the offer, a subscriber on the Billings tab. */
  onSwitchPlan: (cycle: BillingCycleId, code: string) => void;
  /** A quote is with the server. */
  busy?: boolean;
}

/** Whether a coupon holds on the term being bought. A coupon that names no
 *  terms holds on all of them. */
const holdsOn = (coupon: Coupon, cycle: BillingCycleId): boolean =>
  coupon.cycles.length === 0 || coupon.cycles.includes(cycle);

/**
 * The coupons on offer, and what each of them is for this order.
 *
 * Each row is a ticket: what the coupon is worth on the left, and what this
 * order can do with it on the stub at the right, bitten out of the card at
 * both ends of its perforation.
 *
 * Three states, because a coupon is in one of three relative to what is being
 * bought, and collapsing them into one "Apply" would be the dialog lying about
 * two of them:
 *
 *   - **Apply** — it holds on this term and is not on the order.
 *   - **Already applied** — it is on the order. A status rather than a button:
 *     there is nothing left to press, and taking it off belongs to the field,
 *     which has the code in its box and a cross beside it. Only one coupon can
 *     be on an order, which is said at the top rather than left to be
 *     discovered by applying a second and watching the first vanish.
 *   - **Switch plan** — it is good on a term this is not. The chip beside the
 *     code names that term rather than repeating the button, so the row says
 *     what the coupon is *for* and the button says what to do about it.
 *     Pressing it leaves for the plan list, which is a bigger thing than
 *     applying a code and is why the button says so in its own words rather
 *     than quietly re-pricing the order underneath.
 *
 * Whether a code actually holds is still the server's to say: these buttons
 * send it up and the figures redraw from the answer. The `applicable_plan_codes`
 * this reads is what the *list* says, and it is used to label a button rather
 * than to refuse one.
 */
const AvailableCouponsModal: React.FC<AvailableCouponsModalProps> = ({
  open,
  onClose,
  coupons,
  applied,
  cycle,
  plans,
  onApply,
  onRemove,
  onSwitchPlan,
  busy = false,
}) => {
  const toast = useToast();
  // Which shell this list arrives in, and where the ticket puts its headline
  // - see the two branches at the foot of this file and the stub below.
  const isMobile = useIsMobile();

  /** Which term a coupon would move the order to: the first one it names that
   *  is actually on sale. */
  const switchTo = (coupon: Coupon): SubscriptionPlan | null =>
    plans.find((plan) => coupon.cycles.includes(plan.id)) ?? null;

  /**
   * Applying or removing, with the server's refusal reported rather than
   * dropped.
   *
   * The field behind this dialog prints a refusal under itself, which it can
   * because the code being refused is the one in its box. Here there are
   * several codes and no one line belongs to any of them, so the reason is said
   * as a toast — and the dialog stays open on the list it was refused from. A
   * `void promise` here would be an unhandled rejection and a button that
   * silently did nothing, which is the worst of both.
   */
  const attempt = (what: string, run: () => Promise<unknown>) => () => {
    run().catch((cause) => {
      toast.error(
        what,
        cause instanceof Error ? cause.message : "Please try again."
      );
    });
  };

  const body = () => {
    if (coupons.status === "failed") {
      return (
        <EmptyState
          {...failedEmptyState(
            "We could not load your coupons",
            <Button size="sm" variant="secondary" onClick={coupons.reload}>
              Try again
            </Button>
          )}
        />
      );
    }

    if (coupons.status === "loading") {
      return (
        <p role="status" className="py-8 text-center text-[13px] text-ink-muted">
          Loading your coupons…
        </p>
      );
    }

    // No endpoint yet, or one that answered with nothing.
    //
    // Said as what it is rather than dressed up as an error with a retry on
    // it — nothing was refused and there is nothing to ask again. It is not a
    // dead end either: having no coupons *listed* is a different thing from
    // having no coupon, and somebody given a code by a practitioner who
    // referred them can still use it. So the dialog carries the field itself
    // rather than telling them to go and find the one behind it.
    if (coupons.status === "unavailable" || coupons.coupons.length === 0) {
      return (
        <div className="pb-1">
          <div className="flex flex-col items-center py-6 text-center 3xl:py-8">
            <span
              aria-hidden="true"
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EEF1F4] text-ink-subtle 3xl:h-12 3xl:w-12"
            >
              <TicketPercent className="h-5 w-5 3xl:h-6 3xl:w-6" />
            </span>
            <p className="mt-4 text-[14px] font-semibold leading-[1.4] text-[#00424B]">
              No coupons available right now
            </p>
            <p className="mt-1.5 max-w-[340px] text-[12px] 3xl:text-[13px] leading-[1.6] text-ink-muted">
              Referral and offer coupons will show up here when you have them.
            </p>
          </div>

          {/* A rule with the question set into it, rather than a heading above
              the field: the two halves of this panel are "you have none" and
              "unless somebody gave you one", and the line is what says the
              second is a different case rather than a continuation of the
              first. The label sits on the dialog's own white so the rule
              appears to pass behind it. */}
          <div className="relative py-1 text-center">
            <span
              aria-hidden="true"
              className="absolute inset-x-0 top-1/2 h-px bg-line"
            />
            <span className="relative bg-white px-3 text-[13px] 3xl:text-[14px] text-ink-muted">
              Have a code?
            </span>
          </div>

          <div className="mt-4">
            <CouponInput
              // Its own id: the checkout's field is still in the document
              // behind this dialog, and two controls cannot answer to one
              // label.
              id="dialog-coupon-code"
              applied={applied}
              onApply={onApply}
              onRemove={onRemove}
              busy={busy}
            />
          </div>
        </div>
      );
    }

    return (
      <ul className="flex flex-col gap-3">
        {coupons.coupons.map((coupon) => {
          const onOrder = coupon.code === applied?.code;
          const usable = holdsOn(coupon, cycle);
          const target = usable ? null : switchTo(coupon);

          return (
            <li
              key={coupon.code}
              // `relative` for the notches, which hang off the top and bottom
              // edges. Not `overflow-hidden`: clipping them would leave two
              // half-circles sitting inside the card rather than bitten out
              // of it.
              className={`relative flex flex-col rounded-md border sm:flex-row sm:items-stretch ${
                onOrder ? "border-[#5BD3E1] bg-[#F4FDFE]" : "border-line bg-white"
              }`}
            >
              <div className="min-w-0 flex-1 p-4 sm:p-5">
                <p className="flex flex-wrap items-center gap-2">
                  <span className="text-[14px] 3xl:text-[15px] font-semibold tracking-wide text-[#00424B]">
                    {coupon.code}
                  </span>
                  {/* What this row is, said beside the code: the term it is
                      good on where that is not the one being bought, and
                      otherwise what the button opposite will do with it. The
                      applied row carries no chip — its status takes the
                      button's place on the right, where there is no action
                      left to offer. Nor does a coupon good only on terms that
                      are not on sale: there is nothing to apply it to and
                      nowhere to switch to, and an "Apply" chip over a dead
                      button would be the row describing an action it cannot
                      offer. */}
                  {!onOrder && (usable || target) && (
                    <span
                      className={`rounded-[4px] px-2 py-0.5 text-[10px] 3xl:text-[11px] font-semibold uppercase tracking-wide ${
                        target
                          ? "bg-[#EEF1F4] text-ink-muted"
                          : "bg-[#F3EEFF] text-[#6639CE]"
                      }`}
                    >
                      {target ? planTermLabel(target.months) : "Apply"}
                    </span>
                  )}
                </p>
                {/* What it is worth, under the code on a wide screen. On a
                    phone it moves down to the stub, opposite the button, which
                    is what the perforation across the card is there to
                    separate - see below. */}
                {!isMobile && coupon.headline && (
                  <p className="mt-1 text-[13px] 3xl:text-[14px] font-semibold text-[#00A86B]">
                    {coupon.headline}
                  </p>
                )}
                {coupon.description && (
                  <p className="mt-1.5 text-[13px] 3xl:text-[14px] leading-[1.5] text-ink-muted">
                    {coupon.description}
                  </p>
                )}
              </div>

              {/* The stub of the ticket: a perforated line with a bite out of
                  each end, which is what makes the shape read as something
                  torn off rather than as a card with a rule down it.

                  It turns with the card. Side by side from `sm` up, the
                  perforation runs down between the two halves; stacked on a
                  phone it runs across, between what the coupon is and what
                  this order can do with it - and the bites move from the top
                  and bottom edges to the left and right ones with it. A card
                  that kept the vertical rule when stacked would be drawing it
                  across nothing, which is what this used to do. */}
              <div className="relative flex flex-shrink-0 items-center justify-between gap-3 border-t border-dashed border-[#D7DFE3] p-4 sm:w-[168px] sm:justify-center sm:border-l sm:border-t-0 sm:p-5">
                {/* Half a circle rather than a whole one, and that is the
                    difference between a bite and a bubble.

                    A full ring drawn over the card reads as a circle sitting
                    on it. These are open at the outside edge — `border-t-0`
                    on the top one, `border-b-0` on the bottom — so what is
                    left is a single arc whose ends meet the card's own
                    border, which is what makes the edge look torn through.

                    `-top-px` / `-bottom-px` to sit on the border line itself:
                    an absolutely positioned child is placed against the
                    padding box, so at `top-0` the card's border would still
                    be drawn across the mouth of the bite. The white fill then
                    covers both that border and the dashed line behind it,
                    which is what gives the perforation its gap at each
                    end. */}
                <span
                  aria-hidden="true"
                  className={`absolute -top-px left-0 hidden h-2 w-4 -translate-x-1/2 rounded-b-full border border-t-0 bg-white sm:block ${
                    onOrder ? "border-[#5BD3E1]" : "border-line"
                  }`}
                />
                <span
                  aria-hidden="true"
                  className={`absolute -bottom-px left-0 hidden h-2 w-4 -translate-x-1/2 rounded-t-full border border-b-0 bg-white sm:block ${
                    onOrder ? "border-[#5BD3E1]" : "border-line"
                  }`}
                />

                {/* The same two bites, turned a quarter: on the card's left
                    and right edges, centred on the perforation that runs
                    across it while the halves are stacked. `-left-px` and
                    `-right-px` put the white fill over the card's own border
                    so the mouth of each bite is open rather than ruled
                    shut. */}
                <span
                  aria-hidden="true"
                  className={`absolute -left-px top-0 h-4 w-2 -translate-y-1/2 rounded-r-full border border-l-0 bg-white sm:hidden ${
                    onOrder ? "border-[#5BD3E1]" : "border-line"
                  }`}
                />
                <span
                  aria-hidden="true"
                  className={`absolute -right-px top-0 h-4 w-2 -translate-y-1/2 rounded-l-full border border-r-0 bg-white sm:hidden ${
                    onOrder ? "border-[#5BD3E1]" : "border-line"
                  }`}
                />

                {/* What it is worth, on the stub rather than above it - the
                    phone's half of the move described on the headline up
                    there. Rendered even when there is none, so the button
                    opposite keeps the right edge `justify-between` gives
                    it. */}
                {isMobile && (
                  <span className="min-w-0 truncate text-[13px] font-semibold text-[#00A86B]">
                    {coupon.headline}
                  </span>
                )}

                {onOrder ? (
                  // The status, where the button would be. There is nothing to
                  // press on a coupon that is already on the order — taking it
                  // off is the field's job, which has the code in its box and
                  // a cross beside it.
                  <span className="rounded-full bg-[#D9F7E3] px-2.5 py-1 text-[10px] 3xl:text-[11px] font-semibold uppercase tracking-wide text-[#00844A]">
                    Already applied
                  </span>
                ) : target ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => onSwitchPlan(target.id, coupon.code)}
                    // Full width of the stub beside the card; sized by its own
                    // word on the one under it, where the headline has the
                    // other end of the row.
                    className="max-sm:flex-shrink-0 sm:w-full"
                    // The button says "Switch plan"; the name says which plan,
                    // and that pressing it goes somewhere to pick it rather
                    // than switching the order underneath.
                    aria-label={`Switch plan: choose the ${planTermLabel(
                      target.months
                    )} to use ${coupon.code}`}
                  >
                    Switch plan
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    loading={busy}
                    // A coupon good only on terms that are not on sale: nothing
                    // to apply it to and nothing to switch to either.
                    disabled={!usable}
                    onClick={attempt("Could not apply that coupon", () =>
                      onApply(coupon.code)
                    )}
                    className="max-sm:flex-shrink-0 sm:w-full"
                  >
                    Apply
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  /**
   * The heading and the line under it, as one block.
   *
   * Drawn here rather than handed to either shell's own title, because the two
   * are read together: the line says what to *do* with the list, and a title
   * bar's padding plus the body's would set them nearly thirty pixels apart,
   * reading as a heading and then an unrelated sentence.
   */
  const heading = (
    <div>
      <h2 className="text-[17px] 3xl:text-[19px] font-medium text-ink">
        Available coupons
      </h2>
      <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
        Only one coupon can be used per order.
      </p>
    </div>
  );

  if (!open) return null;

  /**
   * On a phone this is a drawer up from the bottom edge, not a panel in the
   * middle of the screen.
   *
   * The same reasoning `ActionDialog` is built on, and it applies twice over
   * here: the list is reached from a row near the top of a long checkout, it
   * can run to four or five tickets, and every one of them ends in a button
   * that has to be pressed with a thumb. Centred, it opened over the figures
   * it was about to change and put its buttons at arm's length. This is not
   * `ActionDialog` itself because that one is for a dialog that asks one
   * thing, with its actions in a footer - this carries a heading of its own
   * and a list that scrolls under it.
   */
  if (isMobile) {
    return (
      <BottomSheet
        open
        title="Available coupons"
        // The sheet's own heading would be the title alone; the block above
        // carries the line under it, so the title is kept for the screen
        // reader and the visible one is drawn in the body.
        hideTitle
        onClose={onClose}
      >
        <div className="px-5 pb-1 pt-1">{heading}</div>
        {/* The rule the dialog gets from its title bar, drawn by hand here -
            full width, so it reads as the sheet's own edge rather than as a
            line inside the list. */}
        <div className="mt-4 border-t border-line" />
        <div className="px-5 pt-4">{body()}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open
      title="Available coupons"
      onClose={onClose}
      // `title` is still passed and still names the dialog - `hideTitle` only
      // takes it off the screen, not out of the accessibility tree - so the
      // visible heading can be the one that is laid out properly.
      hideTitle
      header={heading}
      scrollBody
      className="max-w-[680px]"
    >
      {body()}
    </Modal>
  );
};

export default AvailableCouponsModal;
